import * as React from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  CustomInput,
  Label,
  Navbar,
  NavbarBrand,
  Row,
} from 'reactstrap';
import { useContext, useState } from 'react';
import {
  DomainContext,
  FILE_TYPE_BIRTH_WORKER,
  FILE_TYPE_CULTURAL_CONNECTIONS,
  FILE_TYPE_FAMILY_PRESERVATION,
  FILE_TYPE_INTAKE,
  FILE_TYPE_JORDANS_PRINCIPLE,
  FILE_TYPE_JUSTICE,
  FILE_TYPE_POST_MAJORITY_SUPPORT_SERVICES,
  FILE_TYPE_WELLNESS_CENTRE,
} from '../../contexts/DomainContext';
import { authAxios } from '../../services/AxiosService';
import { saveAs } from 'file-saver';
import DatePicker from 'react-datepicker';
import { formatDateForServer } from '../../formatters/DateTimeFormatter';

export const DynamicReports: React.FC = () => {
  const { fileTypes } = useContext(DomainContext);
  const allFileTypeIds = fileTypes.map((f) => f.fileTypeId);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>(allFileTypeIds);
  const defaultFromDate = new Date();
  const [fromDate, setFromDate] = useState<Date>(
    new Date(defaultFromDate.getMonth() < 3 ? defaultFromDate.getFullYear() - 1 : defaultFromDate.getFullYear(), 3, 1)
  );
  const defaultToDate = new Date();
  const [toDate, setToDate] = useState<Date>(defaultToDate);

  const handleSelectedColumnsChange = (column: string) => {
    setSelectedColumns((prevState) =>
      prevState.includes(column) ? prevState.filter((_) => _ !== column) : [...prevState, column]
    );
  };

  const handleSelectedFileTypesChange = (column: string) => {
    setSelectedFileTypes((prevState) =>
      prevState.includes(column) ? prevState.filter((_) => _ !== column) : [...prevState, column]
    );
  };

  const exportData = () => {
    if (fromDate && toDate) {
      const data = {
        selectedColumns: selectedColumns,
        selectedFileTypes: selectedFileTypes,
        fromDate: formatDateForServer(fromDate),
        toDate: formatDateForServer(toDate),
      };
      authAxios
        .post('/api/reports/dynamic-file-client-request-export', data, { responseType: 'blob' })
        .then((response) => saveAs(response.data, 'CYFN Dynamic Report.csv'));
    }
  };

  const checkListsSameValues = (listA: string[], listB: string[]) => {
    if (listA.length !== listB.length) {
      return false;
    }
    const sortedListA = listA.sort();
    const sortedListB = listB.sort();
    return sortedListA.every((val, index) => val === sortedListB[index]);
  };

  return (
    <div>
      <Navbar color={'light'} light={true} expand={'xs'} className={'border-bottom d-flex flex-wrap sticky-top'}>
        <NavbarBrand className={'w-mobile-100'}>Dynamic Reports</NavbarBrand>
      </Navbar>
      <Container className={'my-5 container--no-margin-print'}>
        <Row>
          <Col>
            <Card>
              <CardHeader>Select Date Range</CardHeader>
              <CardBody>
                <Row>
                  <Col>
                    <Label className={'flex second-label'} style={{ paddingRight: '10px' }}>
                      From
                    </Label>
                    <DatePicker
                      className={'form-control date-select flex'}
                      selected={fromDate}
                      onChange={(date) => {
                        if (date) {
                          setFromDate(date);
                        }
                      }}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      shouldCloseOnSelect={true}
                      dateFormat={'yyyy-MM-dd'}
                    />
                  </Col>
                  <Col>
                    <Label
                      className={'flex second-label'}
                      for={'EndDate'}
                      style={{ paddingRight: '10px', paddingLeft: '10px' }}
                    >
                      To
                    </Label>
                    <DatePicker
                      className={'form-control date-select flex'}
                      selected={toDate}
                      onChange={(date) => {
                        if (date) {
                          setToDate(date);
                        }
                      }}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      shouldCloseOnSelect={true}
                      dateFormat={'yyyy-MM-dd'}
                    />
                  </Col>
                </Row>
                <p className={'text-muted mt-2'}>* Filters files based on the date they were created.</p>
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Card className={'mb-3'}>
              <CardHeader>Select File Types</CardHeader>
              <CardBody>
                <Row>
                  <Col>
                    <Label className={'flex second-label'} for={'fileTypeColumns'} style={{ paddingRight: '10px' }}>
                      File Types
                    </Label>
                    <CustomInput
                      type='checkbox'
                      id={'intake'}
                      name={'intake'}
                      label={'Intake'}
                      checked={selectedFileTypes.includes(FILE_TYPE_INTAKE)}
                      onChange={() => {
                        handleSelectedFileTypesChange(FILE_TYPE_INTAKE);
                      }}
                    />
                    <CustomInput
                      type='checkbox'
                      id={'birthWorker'}
                      name={'birthWorker'}
                      label={'Birth Worker'}
                      checked={selectedFileTypes.includes(FILE_TYPE_BIRTH_WORKER)}
                      onChange={() => {
                        handleSelectedFileTypesChange(FILE_TYPE_BIRTH_WORKER);
                      }}
                    />
                    <CustomInput
                      type='checkbox'
                      id={'culturalConnections'}
                      name={'culturalConnections'}
                      label={'Cultural Connections'}
                      checked={selectedFileTypes.includes(FILE_TYPE_CULTURAL_CONNECTIONS)}
                      onChange={() => {
                        handleSelectedFileTypesChange(FILE_TYPE_CULTURAL_CONNECTIONS);
                      }}
                    />
                    <CustomInput
                      type='checkbox'
                      id={'clientServices'}
                      name={'clientServices'}
                      label={'Client Services'}
                      checked={selectedFileTypes.includes(FILE_TYPE_FAMILY_PRESERVATION)}
                      onChange={() => {
                        handleSelectedFileTypesChange(FILE_TYPE_FAMILY_PRESERVATION);
                      }}
                    />
                    <CustomInput
                      type='checkbox'
                      id={'postMajoritySupportServices'}
                      name={'postMajoritySupportServices'}
                      label={'Post Majority Support Services'}
                      checked={selectedFileTypes.includes(FILE_TYPE_POST_MAJORITY_SUPPORT_SERVICES)}
                      onChange={() => {
                        handleSelectedFileTypesChange(FILE_TYPE_POST_MAJORITY_SUPPORT_SERVICES);
                      }}
                    />
                    <CustomInput
                      type='checkbox'
                      id={'jordansPrinciple'}
                      name={'jordansPrinciple'}
                      label={"Jordan's Principle"}
                      checked={selectedFileTypes.includes(FILE_TYPE_JORDANS_PRINCIPLE)}
                      onChange={() => {
                        handleSelectedFileTypesChange(FILE_TYPE_JORDANS_PRINCIPLE);
                      }}
                    />
                    <CustomInput
                      type='checkbox'
                      id={'wellnessCentre'}
                      name={'wellnessCentre'}
                      label={'Wellness Centre'}
                      checked={selectedFileTypes.includes(FILE_TYPE_WELLNESS_CENTRE)}
                      onChange={() => {
                        handleSelectedFileTypesChange(FILE_TYPE_WELLNESS_CENTRE);
                      }}
                    />
                    <CustomInput
                      type='checkbox'
                      id={'justice'}
                      name={'justice'}
                      label={'Justice'}
                      checked={selectedFileTypes.includes(FILE_TYPE_JUSTICE)}
                      onChange={() => {
                        handleSelectedFileTypesChange(FILE_TYPE_JUSTICE);
                      }}
                    />
                    <CustomInput
                      type='checkbox'
                      id={'all'}
                      name={'all'}
                      label={'All'}
                      checked={checkListsSameValues(selectedFileTypes, allFileTypeIds)}
                      disabled={checkListsSameValues(selectedFileTypes, allFileTypeIds)}
                      onChange={() => {
                        setSelectedFileTypes([
                          FILE_TYPE_INTAKE,
                          FILE_TYPE_BIRTH_WORKER,
                          FILE_TYPE_CULTURAL_CONNECTIONS,
                          FILE_TYPE_FAMILY_PRESERVATION,
                          FILE_TYPE_POST_MAJORITY_SUPPORT_SERVICES,
                          FILE_TYPE_JORDANS_PRINCIPLE,
                          FILE_TYPE_WELLNESS_CENTRE,
                          FILE_TYPE_JUSTICE,
                        ]);
                      }}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Card className={'mb-3'}>
          <CardHeader>Select Columns</CardHeader>
          <CardBody>
            <Row>
              <Col>
                <Label className={'flex second-label'} style={{ paddingRight: '10px' }}>
                  File Columns
                </Label>
                <CustomInput
                  type='checkbox'
                  id={'fileType'}
                  name={'fileType'}
                  label={'File Type'}
                  checked={selectedColumns.includes('fileType')}
                  onChange={() => {
                    handleSelectedColumnsChange('fileType');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'fileNumber'}
                  name={'fileNumber'}
                  label={'File Number'}
                  checked={selectedColumns.includes('fileNumber')}
                  onChange={() => {
                    handleSelectedColumnsChange('fileNumber');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'createdDate'}
                  name={'createdDate'}
                  label={'Date Created'}
                  checked={selectedColumns.includes('createdDate')}
                  onChange={() => {
                    handleSelectedColumnsChange('createdDate');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'stage'}
                  name={'stage'}
                  label={'Stage'}
                  checked={selectedColumns.includes('stage')}
                  onChange={() => {
                    handleSelectedColumnsChange('stage');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'staffChecked'}
                  name={'staffChecked'}
                  label={'Staff'}
                  checked={selectedColumns.includes('staff')}
                  onChange={() => {
                    handleSelectedColumnsChange('staff');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'requestCategories'}
                  name={'requestCategories'}
                  label={'Request Categories'}
                  checked={selectedColumns.includes('requestCategories')}
                  onChange={() => {
                    handleSelectedColumnsChange('requestCategories');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'requestsTotal'}
                  name={'requestsTotal'}
                  label={'Requests Total'}
                  checked={selectedColumns.includes('requestsTotal')}
                  onChange={() => {
                    handleSelectedColumnsChange('requestsTotal');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'paymentVendors'}
                  name={'paymentVendors'}
                  label={'Payment Vendors'}
                  checked={selectedColumns.includes('paymentVendors')}
                  onChange={() => {
                    handleSelectedColumnsChange('paymentVendors');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'paymentsTotal'}
                  name={'paymentsTotal'}
                  label={'Payments Total'}
                  checked={selectedColumns.includes('paymentsTotal')}
                  onChange={() => {
                    handleSelectedColumnsChange('paymentsTotal');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'remainingTotalBalance'}
                  name={'remainingTotalBalance'}
                  label={'Remaining Total Balance'}
                  checked={selectedColumns.includes('remainingTotalBalance')}
                  onChange={() => {
                    handleSelectedColumnsChange('remainingTotalBalance');
                  }}
                />
              </Col>
              <Col>
                <Label className={'flex second-label'} for={'clientColumns'} style={{ paddingRight: '10px' }}>
                  Client Columns
                </Label>
                <CustomInput
                  type='checkbox'
                  id={'name'}
                  name={'name'}
                  label={'Name'}
                  checked={selectedColumns.includes('name')}
                  onChange={() => {
                    handleSelectedColumnsChange('name');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'birthDate'}
                  name={'birthDate'}
                  label={'Birth Date'}
                  checked={selectedColumns.includes('birthDate')}
                  onChange={() => {
                    handleSelectedColumnsChange('birthDate');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'gender'}
                  name={'gender'}
                  label={'Gender'}
                  checked={selectedColumns.includes('gender')}
                  onChange={() => {
                    handleSelectedColumnsChange('gender');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'communityChecked'}
                  name={'communityChecked'}
                  label={'Community'}
                  checked={selectedColumns.includes('community')}
                  onChange={() => {
                    handleSelectedColumnsChange('community');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'indigenousAncestry'}
                  name={'indigenousAncestry'}
                  label={'Indigenous Ancestry'}
                  checked={selectedColumns.includes('indigenousAncestry')}
                  onChange={() => {
                    handleSelectedColumnsChange('indigenousAncestry');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'statusNumber'}
                  name={'statusNumber'}
                  label={'Status Number'}
                  checked={selectedColumns.includes('statusNumber')}
                  onChange={() => {
                    handleSelectedColumnsChange('statusNumber');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'phoneNumber'}
                  name={'phoneNumber'}
                  label={'Phone Number'}
                  checked={selectedColumns.includes('phoneNumber')}
                  onChange={() => {
                    handleSelectedColumnsChange('phoneNumber');
                  }}
                />
                <CustomInput
                  type='checkbox'
                  id={'emailAddress'}
                  name={'emailAddress'}
                  label={'Email Address'}
                  checked={selectedColumns.includes('emailAddress')}
                  onChange={() => {
                    handleSelectedColumnsChange('emailAddress');
                  }}
                />
              </Col>
            </Row>
          </CardBody>
        </Card>
        <Button onClick={() => exportData()}>Generate Report</Button>
      </Container>
    </div>
  );
};
